import axios from 'axios';
// config
import { HOST_API } from 'src/config-global';
import { parseApiError } from 'src/utils/parseApiError';
import UserTableFiltersResult from 'src/sections/user/user-table-filters-result';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API || 'https://api.altiv.ai',
});

axiosInstance.interceptors.request.use((config) => {
  const token =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('accessToken') : null;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    const err = new Error(parseApiError(error));
    err.status = error?.response?.status;
    return Promise.reject(err);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/me',
    login: '/login',
    register: '/register',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/blogs',
    filterList: (filter) => `/blogs?filter=${filter}`,
    details: (slug) => `/blogs/slug/${slug}`,
  },
  currencyExchangeRates: {
    list: '/currency-exchange-rates'
  },
  checkout: {
    routingRules: '/checkout/routing-rules',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },

  plan: {
    list: '/plans',
    filterList: (filter) => `/plans?filter=${filter}`,
    details: (id) => `/plans/${id}`,
  },

  profileAnalytics: {
    list: '/running-analytics/list',
    filterList: (filter) => `/running-analytics/list?filter=${filter}`,
    details: (id) => `/running-analytics/list/${id}`,
  },


  batch: {
    list: '/batches',
    filterList: (filter) => `/batches?filter=${filter}`,
    details: (id) => `/batches/${id}`,
  },

  dashboard: {
    count: '/analytics/cards',
  },

  subscriptions: {
    list: '/subscriptions',
    filterList: (filter) => `/subscriptions?filter=${filter}`,
    details: (id) => `/subscriptions/${id}`,
  },

  playSessions: {
    adminList: '/admin/play-sessions',
    adminDetails: (id) => `/admin/play-sessions/${id}`,
    curatorView: (id) => `/admin/play-sessions/${id}/curator-view`,
  },

  learners: {
    progress: '/admin/learners/progress',
    hub: (userId) => `/admin/learners/${userId}/progress`,
  },

  email: {
    list: '/wait-lists',
    filterList: (filter) => `/wait-lists?${filter}`,
    details: (id) => `/wait-lists/${id}`,
  },
  category: {
    list: '/categories',
    filterList: (filter) => `/categories?${filter}`,
    details: (id) => `/categories/${id}`,
  },
  blogAuthor: {
    list: '/blog-authors',
    details: (id) => `/blog-authors/${id}`,
  },
  faqCategory: {
    list: '/faq-categories',
    filterList: (filter) => `/faq-categories?filter=${filter}`,
    details: (id) => `/faq-categories/${id}`,
  },
  faq: {
    list: '/faqs',
    filterList: (filter) => `/faqs?filter=${filter}`,
    details: (id) => `/faqs/${id}`,
  },
  contact: {
    list: '/contact-uses',
    filterList: (filter) => `/contact-uses?${filter}`,
    details: (id) => `/contact-uses/${id}`,
  },
  user: {
    list: '/api/users/list',
    filterList: (filter) => `/api/users/list?${filter}`,
    details: (id) => `/api/users/${id}`, // patch
    search: '/api/user/search',
    delete: (id) => `/user/${id}`,
    events: (userId) => `/user/events/${userId}`
  },
  resume: {
    details: (userId) => `/resumes/resume-by-id/${userId}`
  },
  comments: {
    list: '/comments',
    filterList: (filter) => `/comments/${filter}`,
    details: (blogId) => `/comments/${blogId}`,
  },
  jobs: {
    list: '/jobs',
    filterList: (filter) => `/jobs?filter=${filter}`,
    details: (id) => `/jobs/${id}`,
  },
  cms: {
    plays: '/cms/plays',
    play: (documentId) => `/cms/plays/${documentId}`,
    fullPlay: (documentId) => `/cms/plays/${documentId}/full`,
    fullPlays: '/cms/plays/full',
    importMd: '/cms/plays/import-md',
    importBundle: '/cms/plays/import-bundle',
    importBundleApply: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/import-bundle/apply`,
    exportPlay: (documentId) => `/cms/plays/${documentId}/export`,
    importSnapshotPreview: '/cms/plays/import-snapshot/preview',
    importSnapshotApply: '/cms/plays/import-snapshot/apply',
    campaignDomains: '/cms/campaign-domains',
    pricingTiers: '/cms/pricing-tiers',
    pricingTier: (documentId) => `/cms/pricing-tiers/${documentId}`,
    adminOfferGrant: '/admin/offers/grant',
    pricingPlans: '/cms/pricing-plans',
    pricingPlan: (documentId) => `/cms/pricing-plans/${documentId}`,
    domains: '/cms/domains',
    domain: (documentId) => `/cms/domains/${documentId}`,
    jobFamilies: '/cms/job-families',
    jobFamily: (documentId) => `/cms/job-families/${documentId}`,
    playSteps: (playDocumentId) => `/cms/plays/${playDocumentId}/steps`,
    playStep: (documentId) => `/cms/play-steps/${documentId}`,
    playStepsSeedBundled: (playDocumentId) => `/cms/plays/${playDocumentId}/seed-bundled-steps`,
    playBundledSlugs: '/cms/plays/bundled-slugs',
    promptTokenCatalog: '/cms/prompt-token-catalog',
    playPrompts: (playDocumentId) => `/cms/plays/${playDocumentId}/prompts`,
    playPrompt: (documentId) => `/cms/prompts/${documentId}`,
    playSynthetic: (playDocumentId) => `/cms/plays/${playDocumentId}/synthetic`,
    playSyntheticImportCsv: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/import-csv`,
    playSyntheticImportConfig: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/import-config`,
    playSyntheticImportWizardContext: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/import-wizard-context`,
    playSyntheticParseCsv: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/parse-csv`,
    playSyntheticSaveWizardMapping: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/save-wizard-mapping`,
    playSyntheticLinkSteps: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/synthetic/link-steps`,
    playImportBundleSynthetic: (playDocumentId) =>
      `/cms/plays/${playDocumentId}/import-bundle/synthetic`,
    playSyntheticOne: (documentId) => `/cms/synthetic/${documentId}`,
    playTemplates: (playDocumentId) => `/cms/plays/${playDocumentId}/templates`,
    playTemplate: (documentId) => `/cms/templates/${documentId}`,
    playBadge: (playDocumentId) => `/cms/plays/${playDocumentId}/badge`,
    homePage: '/cms/home-page',
    announcementBanner: '/cms/announcement-banner',
    cmsFaqs: '/cms/faqs',
    cmsFaq: (documentId) => `/cms/faqs/${documentId}`,
    testimonials: '/cms/testimonials',
    testimonial: (documentId) => `/cms/testimonials/${documentId}`,
    nativityTemplates: '/cms/nativity-templates',
    nativityTemplate: (documentId) => `/cms/nativity-templates/${documentId}`,
    playPlatformSettings: '/cms/play-platform-settings',
    byodDefault: '/play-sessions/byod-default',
  },

  memory: {
    adminSettings: '/api/memory/admin/settings',
    publicSettings: '/api/memory/settings/public',
    // Phase 6 — Insights surfaces.
    adminCharts: '/api/memory/admin/charts',
    adminUsers: '/api/memory/admin/users',
    adminUserActivity: (id) => `/api/memory/admin/users/${encodeURIComponent(id)}/activity`,
    adminUserJourney: (id) => `/api/memory/admin/users/${encodeURIComponent(id)}/journey`,
    adminUserFoboDrift: (id) => `/api/memory/admin/users/${encodeURIComponent(id)}/fobo-drift`,
    adminUserContradictions: (id) => `/api/memory/admin/users/${encodeURIComponent(id)}/contradictions`,
    adminUserGraph: (id) => `/api/memory/admin/users/${encodeURIComponent(id)}/graph`,
    adminCohortDrift: (id) => `/api/memory/admin/cohorts/${encodeURIComponent(id)}/drift`,
    adminPersonaClassify: (id) => `/api/memory/admin/personas/classify/${encodeURIComponent(id)}`,
    adminPersonaFunnelTest: '/api/memory/admin/personas/funnel-test',
    adminHealthDigest: '/api/memory/health/digest',
  },

};